// FILE: src/pages/AdminFeedbackDetailPage.jsx
// One course-survey response (plan WP-8 / WP-6, MTO 10.3), loaded from
// GET /api/feedback/:id (404 FEEDBACK_NOT_FOUND). Each of the five topics
// shows its rating as words and stars ("4 out of 5") with the student's
// comment under it, then "How we could improve" and "Anything else".
// Responses sent before the six-topic survey show their single rating and
// comments, marked "Sent before the six-topic survey".
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Alert, Box, Button, Divider, Link, Stack, Typography } from "@mui/material";
import fetchWithAuth from "../utils/fetchWithAuth";
import { DetailList, EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../components/ui";
import { RatingStars } from "../components/admin/AdminBits";
import { formatDate, formatDateTime, personName } from "../components/admin/format";
import {
  LEGACY_NOTE,
  RATED_TOPICS,
  TEXT_TOPICS,
  isLegacySurvey,
  overallRating,
  ratingText,
  surveyStudent,
  textOf
} from "../components/admin/surveyTopics";
import { errorCode, messageFor } from "../i18n/errors";

function Written({ text, empty }) {
  return text ? (
    <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxWidth: "70ch" }}>{text}</Typography>
  ) : (
    <Typography sx={{ color: "text.secondary" }}>{empty}</Typography>
  );
}

function TopicRating({ label, value, comment, last }) {
  const n = Number(value);
  const rated = value !== null && value !== undefined && Number.isFinite(n) && n > 0;
  return (
    <Box component="li" sx={{ py: 2, "&:first-of-type": { pt: 0 }, ...(last ? { pb: 0 } : {}) }}>
      <Typography variant="h4" component="h3" sx={{ mb: 1 }}>
        {label}
      </Typography>
      {rated ? <RatingStars value={n} /> : <Typography sx={{ fontWeight: 600 }}>Not rated</Typography>}
      <Box sx={{ mt: 1.5 }}>
        <Typography component="p" sx={{ fontWeight: 600, color: "text.secondary" }}>
          Comment
        </Typography>
        <Written text={textOf(comment)} empty="No comment." />
      </Box>
      {!last && <Divider sx={{ mt: 2 }} />}
    </Box>
  );
}

export default function AdminFeedbackDetailPage() {
  const { id } = useParams();
  const [state, setState] = useState({ item: null, loading: true, error: null });
  const latest = useRef(0);

  const load = useCallback(() => {
    const req = ++latest.current;
    setState({ item: null, loading: true, error: null });
    fetchWithAuth(`/api/feedback/${encodeURIComponent(id)}`)
      .then((item) => {
        if (req === latest.current) setState({ item, loading: false, error: null });
      })
      .catch((error) => {
        if (req === latest.current) setState({ item: null, loading: false, error: error || "INTERNAL_ERROR" });
      });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const breadcrumbs = [{ label: "Course survey", to: "/admin/feedback" }, { label: "Response" }];
  const { item, loading, error } = state;

  if (!item) {
    if (loading) return <LoadingState variant="page" label="Loading the survey response…" />;
    const missing = ["FEEDBACK_NOT_FOUND", "NOT_FOUND"].includes(errorCode(error));
    return (
      <Box>
        <PageHeader title="Survey response" breadcrumbs={breadcrumbs} />
        {missing ? (
          <EmptyState
            title={messageFor("FEEDBACK_NOT_FOUND")}
            description="It may have been removed. Go back to the list to choose another response."
            action={
              <Button component={RouterLink} to="/admin/feedback" variant="contained">
                Back to the course survey
              </Button>
            }
          />
        ) : (
          <ErrorState error={error} title="We couldn't load this response" onRetry={load} />
        )}
      </Box>
    );
  }

  const student = surveyStudent(item);
  const name = personName(student, "a student");
  const email = student?.email;
  const legacy = isLegacySurvey(item);
  const overall = overallRating(item);
  const legacyComments = textOf(item.legacy?.comments ?? item.freeText ?? item.comments);

  return (
    <Box>
      <PageHeader
        title="Survey response"
        breadcrumbs={breadcrumbs}
        description={`From ${name}, sent ${formatDate(item.createdAt)}.`}
        actions={
          <Button component={RouterLink} to="/admin/feedback" variant="outlined">
            Back to the course survey
          </Button>
        }
      />

      {legacy && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          {LEGACY_NOTE}: it asked for one overall rating and one comment.
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          alignItems: "start",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.6fr) minmax(0, 1fr)" }
        }}
      >
        {legacy ? (
          <Stack spacing={2.5}>
            <Section id="rating" title="Rating">
              <Typography component="p" sx={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.2, mb: 1 }}>
                {overall === null ? "Not rated" : ratingText(overall, 0)}
              </Typography>
              {overall !== null && <RatingStars value={overall} showText={false} />}
            </Section>
            <Section id="comments" title="Comments">
              <Written text={legacyComments} empty="The student didn't leave any comments." />
            </Section>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <Section id="ratings" title="Ratings by topic" description="Each topic is rated from 1 (poor) to 5 (very good).">
              <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                {RATED_TOPICS.map((t, i) => (
                  <TopicRating
                    key={t.id}
                    label={t.label}
                    value={item.ratings?.[t.id]}
                    comment={item.comments?.[t.id]}
                    last={i === RATED_TOPICS.length - 1}
                  />
                ))}
              </Box>
            </Section>
            {TEXT_TOPICS.map((t) => (
              <Section key={t.id} id={t.id} title={t.label}>
                <Written text={textOf(item[t.id])} empty={t.id === "suggestions" ? "No suggestions." : "Nothing else added."} />
              </Section>
            ))}
          </Stack>
        )}

        <Section id="about" title="About this response">
          <DetailList
            columns={1}
            items={[
              { label: "Student", value: name },
              { label: "Email", value: email ? <Link href={`mailto:${email}`}>{email}</Link> : "—" },
              { label: "Sent", value: formatDateTime(item.createdAt) },
              { label: "Overall rating", value: overall === null ? "Not rated" : ratingText(overall) },
              { label: "Survey", value: legacy ? LEGACY_NOTE : "Six-topic survey" },
              item.enrolmentStartedAt && { label: "Enrolment started", value: formatDate(item.enrolmentStartedAt) }
            ]}
          />
        </Section>
      </Box>
    </Box>
  );
}
